/**
 * @file PDF 处理相关 utils
 */
import { htmlToCanvas } from './dom';
/**
 * 将指定 html 元素转为 jsPDF 对象.
 * 内置了一些预处理逻辑:
 * 1. 目标元素内带有 data-html-to-canvas-remove 属性的元素都会在打印前被移除
 *
 * @category PDF
 * @param args - Argument object
 * @param args.el - 目标元素
 * @param args.orientation - 指定打印方向, 默认逻辑为当元素宽 > 高时横向, 否则纵向
 * @param args.padding - 页边距
 * @param args.padding.top - 上边距
 * @param args.padding.right - 右边距
 * @param args.padding.bottom - 下边距
 * @param args.padding.left - 左边距
 * @param args.preprocess - 打印前的预处理, 一般用于对打印样式进行调整 (在 clone 的元素中进行, 不影响原元素)
 * @param args.backgroundColor - 背景色
 * @returns Promise
 */
function htmlToPDF({ el, preprocess, padding = {}, backgroundColor, orientation, }) {
    const { top = 0, right = 0, bottom = 0, left = 0 } = padding;
    const startPointX = left;
    const startPointY = top || 0;
    const addedWidth = right + left;
    const addedHeight = top + bottom;
    return new Promise(resolve => {
        // scale 以增强清晰度
        htmlToCanvas({
            el,
            preprocess,
            backgroundColor,
            scale: 3,
        }).then(canvas => {
            const imgData = canvas.toDataURL('image/jpeg', 0.9);
            import('jspdf').then(({ default: JSPDF }) => {
                const doc = new JSPDF({
                    orientation: (orientation !== null && orientation !== void 0 ? orientation : canvas.width > canvas.height)
                        ? 'landscape'
                        : 'portrait',
                    unit: 'px',
                    format: [canvas.width + addedWidth, canvas.height + addedHeight],
                });
                doc.addImage(imgData, 'JPEG', startPointX, startPointY, canvas.width + startPointX, canvas.height + startPointY);
                resolve(doc);
            });
        });
    });
}
/**
 * 将指定 html 元素打印并下载为 pdf 文件.
 * 内置了一些预处理逻辑:
 * 1. 目标元素内带有 data-html-to-canvas-remove 属性的元素都会在打印前被移除
 *
 * @category PDF
 * @param args - Argument object
 * @param args.el - 目标元素
 * @param args.name - 下载的文件名, 应为 xx.pdf
 * @param args.orientation - 指定打印方向, 默认逻辑为当元素宽 > 高时横向, 否则纵向
 * @param args.padding - 页边距
 * @param args.padding.top - 上边距
 * @param args.padding.right - 右边距
 * @param args.padding.bottom - 下边距
 * @param args.padding.left - 左边距
 * @param args.preprocess - 打印前的预处理, 一般用于对打印样式进行调整 (在 clone 的元素中进行, 不影响原元素)
 * @param args.backgroundColor - 背景色
 * @returns Promise
 */
export async function downloadHtmlAsPDF(args) {
    const doc = await htmlToPDF(args);
    doc.save(args.name);
}
/**
 * 将指定 html 元素转为 Blob 对象.
 * 内置了一些预处理逻辑:
 * 1. 目标元素内带有 data-html-to-canvas-remove 属性的元素都会在打印前被移除
 *
 * @category PDF
 * @param args - Argument object
 * @param args.el - 目标元素
 * @param args.orientation - 指定打印方向, 默认逻辑为当元素宽 > 高时横向, 否则纵向
 * @param args.padding - 页边距
 * @param args.padding.top - 上边距
 * @param args.padding.right - 右边距
 * @param args.padding.bottom - 下边距
 * @param args.padding.left - 左边距
 * @param args.preprocess - 打印前的预处理, 一般用于对打印样式进行调整 (在 clone 的元素中进行, 不影响原元素)
 * @param args.backgroundColor - 背景色
 * @returns Promise
 */
export async function htmlToPDFBlob(args) {
    const doc = await htmlToPDF(args);
    return doc.output('blob');
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoicGRmLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vc3JjL2Z1bmN0aW9ucy9wZGYudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUE7O0dBRUc7QUFJSCxPQUFPLEVBQUUsWUFBWSxFQUFFLE1BQU0sT0FBTyxDQUFBO0FBRXBDOzs7Ozs7Ozs7Ozs7Ozs7OztHQWlCRztBQUNILFNBQVMsU0FBUyxDQUFDLEVBQ2pCLEVBQUUsRUFDRixVQUFVLEVBQ1YsT0FBTyxHQUFHLEVBQUUsRUFDWixlQUFlLEVBQ2YsV0FBVyxHQU9aO0lBQ0MsTUFBTSxFQUFFLEdBQUcsR0FBRyxDQUFDLEVBQUUsS0FBSyxHQUFHLENBQUMsRUFBRSxNQUFNLEdBQUcsQ0FBQyxFQUFFLElBQUksR0FBRyxDQUFDLEVBQUUsR0FBRyxPQUFPLENBQUE7SUFDNUQsTUFBTSxXQUFXLEdBQUcsSUFBSSxDQUFBO0lBQ3hCLE1BQU0sV0FBVyxHQUFHLEdBQUcsSUFBSSxDQUFDLENBQUE7SUFDNUIsTUFBTSxVQUFVLEdBQUcsS0FBSyxHQUFHLElBQUksQ0FBQTtJQUMvQixNQUFNLFdBQVcsR0FBRyxHQUFHLEdBQUcsTUFBTSxDQUFBO0lBRWhDLE9BQU8sSUFBSSxPQUFPLENBQUMsT0FBTyxDQUFDLEVBQUU7UUFDM0IsZUFBZTtRQUNmLFlBQVksQ0FBQztZQUNYLEVBQUU7WUFDRixVQUFVO1lBQ1YsZUFBZTtZQUNmLEtBQUssRUFBRSxDQUFDO1NBQ1QsQ0FBQyxDQUFDLElBQUksQ0FBQyxNQUFNLENBQUMsRUFBRTtZQUNmLE1BQU0sT0FBTyxHQUFHLE1BQU0sQ0FBQyxTQUFTLENBQUMsWUFBWSxFQUFFLEdBQUcsQ0FBQyxDQUFBO1lBRW5ELE1BQU0sQ0FBQyxPQUFPLENBQUMsQ0FBQyxJQUFJLENBQUMsQ0FBQyxFQUFFLE9BQU8sRUFBRSxLQUFLLEVBQUUsRUFBRSxFQUFFO2dCQUMxQyxNQUFNLEdBQUcsR0FBRyxJQUFJLEtBQUssQ0FBQztvQkFDcEIsV0FBVyxFQUNULENBQUEsV0FBVyxhQUFYLFdBQVcsY0FBWCxXQUFXLEdBQUksTUFBTSxDQUFDLEtBQUssR0FBRyxNQUFNLENBQUMsTUFBTTt3QkFDekMsQ0FBQyxDQUFDLFdBQVc7d0JBQ2IsQ0FBQyxDQUFDLFVBQVU7b0JBQ2hCLElBQUksRUFBRSxJQUFJO29CQUNWLE1BQU0sRUFBRSxDQUFDLE1BQU0sQ0FBQyxLQUFLLEdBQUcsVUFBVSxFQUFFLE1BQU0sQ0FBQyxNQUFNLEdBQUcsV0FBVyxDQUFDO2lCQUNqRSxDQUFDLENBQUE7Z0JBRUYsR0FBRyxDQUFDLFFBQVEsQ0FDVixPQUFPLEVBQ1AsTUFBTSxFQUNOLFdBQVcsRUFDWCxXQUFXLEVBQ1gsTUFBTSxDQUFDLEtBQUssR0FBRyxXQUFXLEVBQzFCLE1BQU0sQ0FBQyxNQUFNLEdBQUcsV0FBVyxDQUM1QixDQUFBO2dCQUVELE9BQU8sQ0FBQyxHQUFHLENBQUMsQ0FBQTtZQUNkLENBQUMsQ0FBQyxDQUFBO1FBQ0osQ0FBQyxDQUFDLENBQUE7SUFDSixDQUFDLENBQUMsQ0FBQTtBQUNKLENBQUM7QUFFRDs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBa0JHO0FBQ0gsTUFBTSxDQUFDLEtBQUssVUFBVSxpQkFBaUIsQ0FBQyxJQU92QztJQUNDLE1BQU0sR0FBRyxHQUFHLE1BQU0sU0FBUyxDQUFDLElBQUksQ0FBQyxDQUFBO0lBQ2pDLEdBQUcsQ0FBQyxJQUFJLENBQUMsSUFBSSxDQUFDLElBQUksQ0FBQyxDQUFBO0FBQ3JCLENBQUM7QUFFRDs7Ozs7Ozs7Ozs7Ozs7Ozs7R0FpQkc7QUFDSCxNQUFNLENBQUMsS0FBSyxVQUFVLGFBQWEsQ0FBQyxJQU1uQztJQUNDLE1BQU0sR0FBRyxHQUFHLE1BQU0sU0FBUyxDQUFDLElBQUksQ0FBQyxDQUFBO0lBQ2pDLE9BQU8sR0FBRyxDQUFDLE1BQU0sQ0FBQyxNQUFNLENBQUMsQ0FBQTtBQUMzQixDQUFDIn0=